import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import LegalPage, { Section, H2, P } from "./components/LegalPage";
import { brand as ds } from "./designSystem";
import { app } from "./firebase";

const BACKEND_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "https://schriftbot-backend.vercel.app";

/* Admin-Dashboard: Nutzer, Nutzung, Abos, Testphase, Widerrufe,
   fehlgeschlagene Kündigungen und Bewertungen. Die Daten kommen aus
   /admin/dashboard; der Server prüft die Admin-UID, diese Seite zeigt ohne
   sie nur „Kein Zugriff". Bewusst schlicht — nur für den Betreiber. */

const TRIAL_STATUS = {
  laeuft: { label: "Läuft", tone: "muted" },
  laeuft_gekuendigt: { label: "Läuft, schon gekündigt", tone: "muted" },
  bezahlt: { label: "Bezahlt", tone: "good" },
  bezahlt_beendet: { label: "Bezahlt, später gekündigt", tone: "good" },
  zahlung_offen: { label: "Zahlung offen", tone: "warn" },
  abgebrochen: { label: "Vor Ende gekündigt", tone: "bad" },
  unbekannt: { label: "Unbekannt", tone: "muted" },
};

const toneColor = {
  good: ds.color.success,
  warn: ds.color.warning,
  bad: ds.color.danger,
  muted: ds.color.muted,
};

const zahl = (n) => (n ?? 0).toLocaleString("de-DE");

const prozent = (teil, ganz) =>
  ganz > 0
    ? `${((teil / ganz) * 100).toLocaleString("de-DE", {
        maximumFractionDigits: 1,
      })} %`
    : "–";

const datum = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "–";

const datumZeit = (iso) =>
  iso
    ? new Date(iso).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";

const kurzTag = (tag) => `${tag.slice(8, 10)}.${tag.slice(5, 7)}.`;

const summe = (reihe) => reihe.reduce((s, p) => s + p.wert, 0);

const raster = {
  display: "grid",
  // 130 px: auf dem iPhone zwei Kacheln nebeneinander
  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
  gap: 12,
  marginTop: 8,
};

const klein = { fontFamily: ds.font.body, fontSize: 13, color: ds.color.muted };

const Kennzahl = ({ label, wert, hinweis }) => (
  <div
    style={{
      background: ds.color.soft,
      borderRadius: ds.radius.md,
      padding: "16px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 0,
    }}
  >
    <span style={{ fontFamily: ds.font.body, fontSize: 14, color: ds.color.muted }}>
      {label}
    </span>
    <span
      style={{
        fontFamily: ds.font.display,
        fontSize: 30,
        lineHeight: 1.1,
        color: ds.color.ink,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {wert}
    </span>
    {hinweis && <span style={klein}>{hinweis}</span>}
  </div>
);

/* Balken pro Tag, eine Reihe, eine Farbe. Antippen oder Überfahren zeigt den
   Tageswert in der Zeile darüber — auf dem iPhone gibt es kein Hover. */
const TagesBalken = ({ titel, reihe, detail }) => {
  const [aktiv, setAktiv] = useState(reihe.length - 1);
  const max = Math.max(1, ...reihe.map((p) => p.wert));
  const breite = 300;
  const hoehe = 120;
  const schritt = breite / reihe.length;
  const balken = Math.max(2, schritt - 2); // 2 px Lücke zwischen den Balken
  const punkt = reihe[aktiv] || reihe[reihe.length - 1];

  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontFamily: ds.font.body, fontSize: 15, fontWeight: 600, color: ds.color.ink }}>
          {titel}
        </span>
        <span style={{ ...klein, fontVariantNumeric: "tabular-nums" }}>
          {kurzTag(punkt.tag)}{" "}
          <strong style={{ color: ds.color.ink }}>{zahl(punkt.wert)}</strong>
          {detail ? ` · ${detail(punkt)}` : ""}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${breite} ${hoehe}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: 120, display: "block", marginTop: 8 }}
        role="img"
        aria-label={`${titel}, letzte ${reihe.length} Tage, zusammen ${zahl(summe(reihe))}`}
      >
        <line
          x1="0"
          x2={breite}
          y1={hoehe - 0.5}
          y2={hoehe - 0.5}
          stroke={ds.color.line}
          strokeWidth="1"
        />
        {reihe.map((p, i) => {
          const h = (p.wert / max) * (hoehe - 4);
          return (
            <g key={p.tag}>
              <rect
                x={i * schritt + 1}
                y={hoehe - Math.max(h, p.wert > 0 ? 2 : 0)}
                width={balken}
                height={Math.max(h, p.wert > 0 ? 2 : 0)}
                rx="1.5"
                fill={ds.color.accent}
                opacity={i === aktiv ? 1 : 0.55}
              />
              {/* Trefferfläche über die ganze Höhe, größer als der Balken */}
              <rect
                x={i * schritt}
                y="0"
                width={schritt}
                height={hoehe}
                fill="transparent"
                onMouseEnter={() => setAktiv(i)}
                onClick={() => setAktiv(i)}
              >
                <title>{`${kurzTag(p.tag)}: ${zahl(p.wert)}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div style={{ ...klein, display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        <span>{kurzTag(reihe[0].tag)}</span>
        <span>max. {zahl(max)}</span>
        <span>{kurzTag(reihe[reihe.length - 1].tag)}</span>
      </div>
    </div>
  );
};

/* Eintrag in einer Liste (Widerrufe, Kündigungen, Bewertungen): gestapelt
   statt Tabelle, damit es auf dem iPhone nicht seitlich scrollt. */
const Eintrag = ({ oben, rechts, children, fehler }) => (
  <div style={{ borderTop: `1px solid ${ds.color.line}`, padding: "12px 0" }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "baseline",
      }}
    >
      <span
        style={{
          fontFamily: ds.font.body,
          fontSize: 15,
          fontWeight: 600,
          color: ds.color.ink,
          overflowWrap: "anywhere",
          minWidth: 0,
        }}
      >
        {oben}
      </span>
      <span style={{ ...klein, flex: "none" }}>{rechts}</span>
    </div>
    {children && (
      <div
        style={{
          fontFamily: ds.font.body,
          fontSize: 14,
          lineHeight: 1.5,
          color: ds.color.muted,
          marginTop: 4,
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
        }}
      >
        {children}
      </div>
    )}
    {fehler && (
      <div
        style={{
          fontFamily: ds.font.body,
          fontSize: 14,
          lineHeight: 1.5,
          fontWeight: 600,
          color: ds.color.danger,
          marginTop: 4,
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
        }}
      >
        {fehler}
      </div>
    )}
  </div>
);

/* Woran eine Kündigung gescheitert ist — Grund, was Stripe dazu sagt, und
   ob die Bestätigungsmail rausging. Ältere Einträge haben nur `grund`. */
const KUENDIGUNG_GRUND = {
  "kein-konto": "Kein Stripe-Kunde mit dieser E-Mail",
  "kein-aktives-abo": "Kein laufendes Abo bei Stripe",
  fehler: "Fehler beim Kündigen",
};

const kuendigungFehler = (k) =>
  [
    KUENDIGUNG_GRUND[k.grund] ||
      (k.status === "bestaetigung-fehlgeschlagen"
        ? "Gekündigt, aber Bestätigungsmail nicht versendet"
        : k.grund),
    k.stripeStatus && `Stripe: ${k.stripeStatus}`,
    k.fehler,
    k.bestaetigung?.ok === false && `Mail: ${k.bestaetigung.fehler}`,
  ]
    .filter(Boolean)
    .join("\n");

const widerrufFehler = (w) =>
  [
    w.testphaseFehler && `Testphase nicht beendet – ${w.testphaseFehler}`,
    w.bestaetigung?.ok === false &&
      `Bestätigungsmail fehlgeschlagen: ${w.bestaetigung.fehler}`,
  ]
    .filter(Boolean)
    .join("\n");

const Leer = ({ children }) => (
  <P muted style={{ marginBottom: 0 }}>
    {children}
  </P>
);

export default function AdminStats() {
  const auth = useMemo(() => getAuth(app), []);
  const [user, setUser] = useState(undefined); // undefined = lädt noch
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [alleTrials, setAlleTrials] = useState(false);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => onAuthStateChanged(auth, setUser), [auth]);

  const laden = useCallback(
    async (frisch = false) => {
      if (!user) return;
      setLaedt(true);
      setFehler(null);
      try {
        const token = await user.getIdToken();
        const res = await fetch(
          `${BACKEND_BASE_URL}/admin/dashboard${frisch ? "?frisch=1" : ""}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.status === 403) throw new Error("Kein Zugriff mit diesem Konto.");
        if (!res.ok) throw new Error(`Server antwortet mit ${res.status}.`);
        setDaten(await res.json());
      } catch (err) {
        setFehler(err.message);
      } finally {
        setLaedt(false);
      }
    },
    [user]
  );

  useEffect(() => {
    laden();
  }, [laden]);

  const trial = useMemo(() => {
    if (!daten) return null;
    const liste = daten.testphase.liste;
    const zaehle = (...status) =>
      liste.filter((t) => status.includes(t.status)).length;
    const laufen = zaehle("laeuft", "laeuft_gekuendigt");
    const bezahlt = zaehle("bezahlt", "bezahlt_beendet");
    return {
      gesamt: liste.length,
      laufen,
      bezahlt,
      beendet: liste.length - laufen - zaehle("unbekannt"),
      abgebrochen: zaehle("abgebrochen"),
      offen: zaehle("zahlung_offen"),
    };
  }, [daten]);

  const widerrufeFehlgeschlagen = daten
    ? daten.widerrufe.filter((w) => widerrufFehler(w)).length
    : 0;

  const downloadsReihe = useMemo(
    () =>
      daten
        ? daten.nutzung.downloadsProTag.map((p) => ({ ...p, wert: p.pdf + p.bild }))
        : [],
    [daten]
  );

  return (
    <LegalPage wide title="Dashboard">
      {user === undefined && (
        <Section>
          <Leer>Lädt …</Leer>
        </Section>
      )}

      {user === null && (
        <Section>
          <P>Bitte mit dem Admin-Konto anmelden.</P>
          <button
            className="sb-btn sb-btn-primary"
            onClick={() => signInWithPopup(auth, new GoogleAuthProvider())}
          >
            Mit Google anmelden
          </button>
        </Section>
      )}

      {user && fehler && (
        <Section>
          <P>{fehler}</P>
        </Section>
      )}

      {user && !daten && !fehler && (
        <Section>
          <Leer>Lädt …</Leer>
        </Section>
      )}

      {user && daten && (
        <>
          {/* ── Überblick ── */}
          <Section>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <H2>Überblick</H2>
              <button
                className="sb-btn sb-btn-secondary"
                disabled={laedt}
                onClick={() => laden(true)}
              >
                {laedt ? "Lädt …" : "Aktualisieren"}
              </button>
            </div>
            <div style={raster}>
              <Kennzahl
                label="Nutzer"
                wert={zahl(daten.nutzer.gesamt)}
                hinweis={`+${zahl(summe(daten.nutzer.neuProTag))} in 30 Tagen`}
              />
              <Kennzahl
                label="Zahlende Abos"
                wert={zahl(daten.abos.gesamt)}
                hinweis={`${prozent(daten.abos.gesamt, daten.nutzer.gesamt)} der Nutzer`}
              />
              <Kennzahl
                label="Aufrufe (30 T)"
                wert={zahl(summe(daten.nutzung.aufrufeProTag))}
                hinweis={`${zahl(daten.nutzung.aufrufeGesamt)} gesamt`}
              />
              <Kennzahl
                label="Downloads (30 T)"
                wert={zahl(summe(downloadsReihe))}
                hinweis={`${zahl(daten.nutzung.pdfGesamt + daten.nutzung.bildGesamt)} gesamt`}
              />
              <Kennzahl
                label="Bewertung"
                wert={daten.bewertungen.schnitt.toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                })}
                hinweis={`${zahl(daten.bewertungen.anzahl)} Bewertungen`}
              />
              <Kennzahl
                label="Conversion"
                wert={prozent(trial.bezahlt, trial.beendet)}
                hinweis={`${trial.bezahlt} von ${trial.beendet} beendeten`}
              />
            </div>
          </Section>

          {/* ── Verlauf ── */}
          <Section>
            <H2>Verlauf pro Tag</H2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 28,
                marginTop: 8,
              }}
            >
              <TagesBalken titel="Seitenaufrufe" reihe={daten.nutzung.aufrufeProTag} />
              <TagesBalken
                titel="Downloads"
                reihe={downloadsReihe}
                detail={(p) => `${zahl(p.pdf)} PDF, ${zahl(p.bild)} Bild`}
              />
              <TagesBalken titel="Neue Nutzer" reihe={daten.nutzer.neuProTag} />
            </div>
          </Section>

          {/* ── Abos ── */}
          <Section>
            <H2>Abos</H2>
            <div style={raster}>
              {Object.entries(daten.abos.proTarif)
                .sort((a, b) => b[1] - a[1])
                .map(([tarif, anzahl]) => (
                  <Kennzahl key={tarif} label={tarif} wert={zahl(anzahl)} />
                ))}
            </div>
            <P muted style={{ marginTop: 16, marginBottom: 0, fontSize: 14 }}>
              {zahl(daten.abos.gekuendigt)} zum Monatsende gekündigt ·{" "}
              {zahl(daten.abos.zahlungOffen)} mit offener Zahlung
            </P>
          </Section>

          {/* ── Testphase ── */}
          <Section>
            <H2>Testphase</H2>
            <div style={raster}>
              <Kennzahl label="Angebot gezeigt" wert={zahl(daten.testphase.angezeigt)} />
              <Kennzahl
                label="„Testen“ geklickt"
                wert={zahl(daten.testphase.geklickt)}
                hinweis={`${prozent(daten.testphase.geklickt, daten.testphase.angezeigt)} der Anzeigen`}
              />
              <Kennzahl
                label="Gestartet"
                wert={zahl(trial.gesamt)}
                hinweis={`${trial.laufen} laufen gerade`}
              />
              <Kennzahl
                label="Bezahlt"
                wert={zahl(trial.bezahlt)}
                hinweis={`${trial.abgebrochen} abgebrochen, ${trial.offen} Zahlung offen`}
              />
            </div>
            <div style={{ marginTop: 16 }}>
              {daten.testphase.liste.length === 0 ? (
                <Leer>Noch keine Testphase gestartet.</Leer>
              ) : (
                (alleTrials
                  ? daten.testphase.liste
                  : daten.testphase.liste.slice(0, 30)
                ).map((t, i) => {
                  const s = TRIAL_STATUS[t.status] || TRIAL_STATUS.unbekannt;
                  return (
                    <Eintrag
                      key={i}
                      oben={t.email || "E-Mail unbekannt"}
                      rechts={`${datum(t.startedAt)} – ${datum(t.trialEnd)}`}
                    >
                      <span style={{ color: toneColor[s.tone] }}>{s.label}</span>
                      {t.fehler && `\nStripe: ${t.fehler}`}
                    </Eintrag>
                  );
                })
              )}
              {daten.testphase.liste.length > 30 && (
                <button
                  onClick={() => setAlleTrials((a) => !a)}
                  className="sb-btn sb-btn-secondary"
                  style={{ width: "100%", marginTop: 12 }}
                >
                  {alleTrials
                    ? "Nur die neuesten 30 anzeigen"
                    : `Alle ${daten.testphase.liste.length} anzeigen`}
                </button>
              )}
            </div>
          </Section>

          {/* ── Widerrufe ── */}
          <Section>
            <H2>
              Widerrufe ({daten.widerrufe.length}
              {widerrufeFehlgeschlagen > 0 &&
                ` · ${widerrufeFehlgeschlagen} fehlgeschlagen`}
              )
            </H2>
            {daten.widerrufe.length === 0 ? (
              <Leer>Keine Widerrufe.</Leer>
            ) : (
              daten.widerrufe.map((w) => (
                <Eintrag
                  key={w.id}
                  oben={w.email || "ohne E-Mail"}
                  rechts={datumZeit(w.receivedAt)}
                  fehler={widerrufFehler(w)}
                >
                  {[
                    w.name,
                    w.orderInfo,
                    w.message,
                    w.status && `Status: ${w.status}`,
                    w.testphaseBeendet && "Testphase sofort beendet",
                  ]
                    .filter(Boolean)
                    .join("\n")}
                </Eintrag>
              ))
            )}
          </Section>

          {/* ── Fehlgeschlagene Kündigungen ── */}
          <Section>
            <H2>Fehlgeschlagene Kündigungen ({daten.kuendigungen.length})</H2>
            {daten.kuendigungen.length === 0 ? (
              <Leer>Keine fehlgeschlagenen Kündigungen.</Leer>
            ) : (
              daten.kuendigungen.map((k) => (
                <Eintrag
                  key={k.id}
                  oben={k.email || "ohne E-Mail"}
                  rechts={datumZeit(k.receivedAt)}
                  fehler={kuendigungFehler(k)}
                >
                  {[k.name, k.note]
                    .filter(Boolean)
                    .join("\n")}
                </Eintrag>
              ))
            )}
          </Section>

          {/* ── Bewertungen ── */}
          <Section>
            <H2>Neueste Bewertungen mit Text</H2>
            {daten.bewertungen.neueste.length === 0 ? (
              <Leer>Keine Bewertungen mit Text.</Leer>
            ) : (
              daten.bewertungen.neueste.map((b, i) => (
                <Eintrag
                  key={i}
                  oben={`${"★".repeat(b.rating)}${"☆".repeat(5 - b.rating)}`}
                  rechts={datumZeit(b.at)}
                >
                  {b.text}
                  {b.email ? `\n${b.email}` : ""}
                </Eintrag>
              ))
            )}
          </Section>
        </>
      )}
    </LegalPage>
  );
}
